/**
 * Phase COI-REFRESH (Part B) — small color-coded chip surfacing the
 * COI expiry runway on the admin rental detail page.
 *
 * Fetches ``GET /api/admin/coi/refresh-status/{rental_id}`` on mount.
 * Color bands (per phase spec):
 *   >90d      → gray   ("COI expires in N days")
 *   30–90d    → green  ("COI expires in N days")
 *   7–29d     → amber  ("COI expires in N days")
 *   0–6d      → red    ("COI expires in N days")
 *   past 0    → red    ("Expired N days ago" — and INSURANCE LAPSED if flagged)
 *
 * Fails soft: absent ``coi_policy_end_date`` renders nothing so we
 * never spam the chip on rentals whose COI hasn't been recorded yet.
 */
import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { ShieldAlert, ShieldCheck } from "lucide-react";

function classifyDays(daysUntilExpiry) {
    if (daysUntilExpiry == null) return null;
    if (daysUntilExpiry < 0) return "expired";
    if (daysUntilExpiry < 7) return "critical";
    if (daysUntilExpiry < 30) return "warning";
    if (daysUntilExpiry <= 90) return "ok";
    return "healthy";
}

const BAND_CLASSES = {
    healthy: "bg-slate-100 text-slate-700 ring-slate-200",
    ok: "bg-emerald-50 text-emerald-800 ring-emerald-200",
    warning: "bg-amber-50 text-amber-900 ring-amber-200",
    critical: "bg-red-50 text-red-700 ring-red-200",
    expired: "bg-red-50 text-red-700 ring-red-200",
};

export default function CoiStatusChip({ rentalId }) {
    const [status, setStatus] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!rentalId) return;
        let cancelled = false;
        (async () => {
            try {
                const { data } = await api.get(`/admin/coi/refresh-status/${rentalId}`);
                if (!cancelled) setStatus(data);
            } catch {
                // Silent — the chip is optional decoration; a hard error
                // on this fetch (permissions, offline) shouldn't break
                // the page.
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [rentalId]);

    if (loading || !status || status.days_until_expiry == null) return null;

    const band = classifyDays(status.days_until_expiry);
    const lapsed = !!status.insurance_lapsed;
    const label = (() => {
        if (band === "expired") {
            const daysPast = Math.abs(status.days_until_expiry);
            return lapsed
                ? `Insurance lapsed · expired ${daysPast} day${daysPast === 1 ? "" : "s"} ago`
                : `Expired ${daysPast} day${daysPast === 1 ? "" : "s"} ago`;
        }
        const d = status.days_until_expiry;
        return `COI expires in ${d} day${d === 1 ? "" : "s"}`;
    })();

    const Icon = band === "healthy" || band === "ok" ? ShieldCheck : ShieldAlert;

    return (
        <span
            className={
                "inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-[11px] font-semibold ring-1 ring-inset "
                + (BAND_CLASSES[band] || BAND_CLASSES.healthy)
            }
            data-testid="rental-coi-status-chip"
            data-band={band}
        >
            <Icon size={11} aria-hidden="true" />
            {label}
        </span>
    );
}
